import {
  Component,
  OnDestroy,
  HostListener,
  inject,
  AfterViewChecked,
  ElementRef,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export type SvMessageType = 'success' | 'error' | 'warning' | 'info' | 'question';

export interface SvMessageConfig {
  /** Tipo de mensagem */
  type: SvMessageType;
  /** Título da mensagem */
  title?: string;
  /** Texto da mensagem */
  text?: string;
  /** Conteúdo HTML (alternativa ao text) */
  html?: string;
  /** Mostrar como toast (notificação rápida) */
  toast?: boolean;
  /** Posição do toast */
  toastPosition?: 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start' | 'top' | 'bottom';
  /** Tempo em ms para fechar automaticamente */
  timer?: number;
  /** Texto do botão de confirmação */
  confirmButtonText?: string;
  /** Texto do botão de cancelamento */
  cancelButtonText?: string;
  /** Mostrar botão de confirmação */
  showConfirmButton?: boolean;
  /** Mostrar botão de cancelamento */
  showCancelButton?: boolean;
  /** Inverter posição dos botões */
  reverseButtons?: boolean;
  /** Focar no botão cancelar */
  focusCancel?: boolean;
  /** Função de resolução da Promise */
  resolve?: (result: SvMessageResult) => void;
  /** URL de imagem para mostrar em vez do ícone */
  imageUrl?: string;
  /** Largura da imagem em px */
  imageWidth?: number;
  /** Tipo de input — suporta 'text' */
  input?: 'text';
  /** Valor inicial do input */
  inputValue?: string;
  /** Placeholder do input */
  inputPlaceholder?: string;
  /** @internal valor actual do input (binding) */
  _inputCurrentValue?: string;
  /** @internal mensagem de validação do input */
  _inputValidationMessage?: string;
  /** @internal valores capturados dos inputs HTML antes de fechar */
  _htmlInputValues?: Record<string, string>;
  /** @internal posição drag X */
  _dragX?: number;
  /** @internal posição drag Y */
  _dragY?: number;
  /** Largura máxima personalizada (ex: '800px', '90vw') */
  customMaxWidth?: string;
}

export interface SvMessageResult {
  isConfirmed: boolean;
  isDismissed: boolean;
  /** Valor do input (quando input: 'text' é usado) ou true (para compatibilidade SweetAlert2) */
  value?: any;
  /** Valores dos inputs HTML capturados do dialog (quando html com inputs é usado) */
  htmlInputValues?: Record<string, string>;
}

@Component({
  selector: 'app-sv-message',
  templateUrl: './sv-message.component.html',
  styleUrls: ['./sv-message.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
  animations: [
    trigger('overlayAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms ease-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.7) translateY(20px)' }),
        animate('300ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'scale(1) translateY(0)' })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0, transform: 'scale(0.9) translateY(10px)' }))]),
    ]),
    trigger('toastAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%) scale(0.8)' }),
        animate('350ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'translateX(0) scale(1)' })),
      ]),
      transition(':leave', [animate('250ms ease-in', style({ opacity: 0, transform: 'translateX(100%) scale(0.8)' }))]),
    ]),
    trigger('iconBounce', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate('400ms 150ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
    trigger('checkmarkDraw', [
      state('drawn', style({ 'stroke-dashoffset': '0' })),
      transition(':enter', [
        style({ 'stroke-dashoffset': '100' }),
        animate('500ms 350ms ease-out', style({ 'stroke-dashoffset': '0' })),
      ]),
    ]),
  ],
})
export class SvMessageComponent implements OnDestroy, AfterViewChecked {
  /** Lista de mensagens ativas (modais) */
  messages: SvMessageConfig[] = [];

  /** Lista de toasts ativos */
  toasts: (SvMessageConfig & { id: number })[] = [];

  /** Referências aos containers de HTML customizado */
  @ViewChildren('htmlContainer') htmlContainers!: QueryList<ElementRef<HTMLDivElement>>;

  /** Set de configs já renderizadas — impede re-render destrutivo dos inputs */
  private renderedHtmlConfigs = new Set<SvMessageConfig>();

  private toastCounter = 0;
  private timerRefs: Map<number, any> = new Map();
  private sanitizer = inject(DomSanitizer);

  private sanitizeModalHtml(html: string): string {
    if (!html || typeof html !== 'string') return '';
    return html
      .replace(/<\s*(script|iframe|object|embed)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
      .replace(/<\s*(script|iframe|object|embed)\b[^>]*\/?>/gi, '')
      .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(
        /\b(href|src|xlink:href|action|formaction)\s*=\s*(?:"\s*javascript:[^"]*"|'\s*javascript:[^']*'|\s*javascript:[^\s>]+)/gi,
        '$1="#"',
      );
  }

  /**
   * Marca HTML como seguro para renderizar com inputs (bypassSecurityTrustHtml).
   * Necessário porque [innerHTML] remove elementos <input>, <select>, <textarea>.
   */
  trustedHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.sanitizeModalHtml(html));
  }

  // Drag state
  private isDragging = false;
  private dragConfig: SvMessageConfig | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOriginX = 0;
  private dragOriginY = 0;
  private boundOnDragMove = this.onDragMove.bind(this);
  private boundOnDragEnd = this.onDragEnd.bind(this);

  ngOnDestroy(): void {
    this.timerRefs.forEach(ref => clearTimeout(ref));
    this.timerRefs.clear();
    this.renderedHtmlConfigs.clear();
  }

  /**
   * AfterViewChecked — renderiza o HTML customizado uma ÚNICA vez.
   * Impede que o Angular sobrescreva os inputs editados pelo utilizador.
   */
  ngAfterViewChecked(): void {
    if (!this.htmlContainers) return;
    this.htmlContainers.forEach(containerRef => {
      const el = containerRef.nativeElement;
      const msgIdStr = el.getAttribute('data-msg-id');
      if (msgIdStr === null) return;
      const msgIdx = parseInt(msgIdStr, 10);
      const config = this.messages[msgIdx];
      if (!config || !config.html) return;
      // Só renderizar se ainda não foi feito
      if (this.renderedHtmlConfigs.has(config)) return;
      this.renderedHtmlConfigs.add(config);
      el.innerHTML = this.sanitizeModalHtml(config.html);
    });
  }

  /** TrackBy para *ngFor dos toasts */
  trackToastById(_index: number, toast: SvMessageConfig & { id: number }): number {
    return toast.id;
  }

  /**
   * Mostra uma mensagem (modal ou toast)
   */
  show(config: SvMessageConfig): void {
    // sem debug
    if (config.toast) {
      this.showToast(config);
    } else {
      this.showModal(config);
    }
  }

  /**
   * Mostra um modal
   */
  private showModal(config: SvMessageConfig): void {
    // Defaults
    if (config.showConfirmButton === undefined) {
      config.showConfirmButton = true;
    }
    if (!config.confirmButtonText) {
      config.confirmButtonText = 'OK';
    }

    // Compatibilidade temporária mantendo HTML passado para alertas tipados caso a config assim os tenha
    if ((config.type === 'success' || config.type === 'error' || config.type === 'warning') && !config.html) {
      config.html = undefined;
    }

    // Inicializar o valor do input se presente
    if (config.input) {
      config._inputCurrentValue = config.inputValue || '';
      config._inputValidationMessage = '';
    }

    // Desfocar qualquer elemento ativo (ex: campo de pesquisa) para evitar captura residual de teclado
    if (document.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
      (document.activeElement as HTMLElement).blur();
    }

    // Evitar empilhamento de modais idênticos duplicados por cliques repetidos
    const duplicateIdx = this.messages.findIndex(
      m =>
        m.type === config.type &&
        (m.title || '') === (config.title || '') &&
        (m.text || '') === (config.text || '') &&
        (m.html || '') === (config.html || ''),
    );
    if (duplicateIdx > -1) {
      const prev = this.messages[duplicateIdx];
      if (prev.resolve) {
        prev.resolve({ isConfirmed: false, isDismissed: true });
      }
      this.messages.splice(duplicateIdx, 1);
      this.renderedHtmlConfigs.delete(prev);
    }

    this.messages.push(config);

    // Timer
    if (config.timer) {
      const timerId = setTimeout(() => {
        this.dismissModal(config, false);
      }, config.timer);
      this.timerRefs.set(this.toastCounter, timerId);
    }
  }

  /**
   * Mostra um toast
   */
  private showToast(config: SvMessageConfig): void {
    const id = ++this.toastCounter;
    const toast = { ...config, id };
    this.toasts.push(toast);

    const duration = config.timer || 3000;
    const timerId = setTimeout(() => {
      this.dismissToast(id, false);
    }, duration);
    this.timerRefs.set(id, timerId);
  }

  /**
   * Confirma uma mensagem
   */
  confirmModal(config: SvMessageConfig): void {
    // Se tem input, validar que não está vazio
    if (config.input) {
      const val = (config._inputCurrentValue || '').trim();
      if (!val) {
        config._inputValidationMessage = 'Este campo é obrigatório.';
        return;
      }
      config._inputValidationMessage = '';
    }

    // Capturar valores de inputs HTML antes de fechar o modal
    // Permite que dialogs com HTML injectado (ex: partilha de documentos) preservem os dados
    if (config.html) {
      const htmlInputs = document.querySelectorAll(
        '.sv-message-html input, .sv-message-html select, .sv-message-html textarea',
      );
      if (htmlInputs.length > 0) {
        const htmlValues: Record<string, string> = {};
        htmlInputs.forEach((el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
          if (el.id) {
            htmlValues[el.id] = el.value || '';
          }
        });
        config._htmlInputValues = htmlValues;
      }
    }

    this.dismissModal(config, true);
  }

  /**
   * Cancela uma mensagem
   */
  cancelModal(config: SvMessageConfig): void {
    this.dismissModal(config, false);
  }

  /**
   * Remove um modal
   */
  private dismissModal(config: SvMessageConfig, confirmed: boolean): void {
    const idx = this.messages.indexOf(config);
    if (idx > -1) {
      this.messages.splice(idx, 1);
      this.renderedHtmlConfigs.delete(config);
    }
    if (config.resolve) {
      const result: SvMessageResult = {
        isConfirmed: confirmed,
        isDismissed: !confirmed,
      };
      // Adicionar valor do input ao resultado
      if (config.input && confirmed) {
        result.value = (config._inputCurrentValue || '').trim();
      }
      // Incluir valores de inputs HTML (capturados antes do modal fechar)
      if (confirmed && config._htmlInputValues) {
        result.htmlInputValues = config._htmlInputValues;
      }
      config.resolve(result);
    }
  }

  /**
   * Remove um toast
   */
  dismissToast(id: number, confirmed: boolean): void {
    const idx = this.toasts.findIndex(t => t.id === id);
    if (idx > -1) {
      const toast = this.toasts[idx];
      this.toasts.splice(idx, 1);
      if (toast.resolve) {
        toast.resolve({
          isConfirmed: confirmed,
          isDismissed: !confirmed,
        });
      }
    }
    if (this.timerRefs.has(id)) {
      clearTimeout(this.timerRefs.get(id));
      this.timerRefs.delete(id);
    }
  }

  /**
   * Fechar com ESC
   */
  @HostListener('document:keydown.escape')
  onEscKey(): void {
    if (this.messages.length > 0) {
      const lastMessage = this.messages[this.messages.length - 1];
      this.dismissModal(lastMessage, false);
    }
  }

  private justDismissedByEnter = false;

  /**
   * Confirmar com Enter
   */
  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(e: Event | KeyboardEvent): void {
    const event = e as KeyboardEvent;
    if (this.messages.length > 0) {
      const target = event.target as HTMLElement;
      if (target && target.tagName && target.tagName.toLowerCase() === 'textarea') {
        return; // Permitir quebras de linha em textareas
      }
      event.preventDefault();
      event.stopPropagation();

      // Desfocar qualquer elemento ativo para prevenir re-foco/duplo Enter acidental
      if (document.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
        (document.activeElement as HTMLElement).blur();
      }

      this.justDismissedByEnter = true;
      setTimeout(() => {
        this.justDismissedByEnter = false;
      }, 500);

      const lastMessage = this.messages[this.messages.length - 1];
      this.confirmModal(lastMessage);
    }
  }

  /**
   * Intercetar keyup do Enter para evitar que o levantamento da tecla dispare eventos em inputs de fundo
   */
  @HostListener('document:keyup.enter', ['$event'])
  onEnterKeyUp(e: Event | KeyboardEvent): void {
    const event = e as KeyboardEvent;
    if (this.justDismissedByEnter) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Clique no overlay (fora do modal)
   */
  onOverlayClick(event: MouseEvent, config: SvMessageConfig): void {
    if ((event.target as HTMLElement).classList.contains('sv-message-overlay')) {
      this.dismissModal(config, false);
    }
  }

  /**
   * Retorna a classe CSS do ícone
   */
  getIconClass(type: SvMessageType): string {
    const map: Record<SvMessageType, string> = {
      success: 'sv-icon-success',
      error: 'sv-icon-error',
      warning: 'sv-icon-warning',
      info: 'sv-icon-info',
      question: 'sv-icon-question',
    };
    return map[type] || 'sv-icon-info';
  }

  /**
   * Retorna o botão cor de fundo por tipo
   */
  getConfirmButtonClass(type: SvMessageType): string {
    const map: Record<SvMessageType, string> = {
      success: 'sv-btn-success',
      error: 'sv-btn-error',
      warning: 'sv-btn-warning',
      info: 'sv-btn-primary',
      question: 'sv-btn-primary',
    };
    return map[type] || 'sv-btn-primary';
  }

  // ─── Drag (mover modal) ───────────────────────────────────

  onDragStart(event: MouseEvent | TouchEvent, config: SvMessageConfig): void {
    this.isDragging = true;
    this.dragConfig = config;

    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    this.dragStartX = clientX;
    this.dragStartY = clientY;
    this.dragOriginX = config._dragX || 0;
    this.dragOriginY = config._dragY || 0;

    document.addEventListener('mousemove', this.boundOnDragMove);
    document.addEventListener('mouseup', this.boundOnDragEnd);
    document.addEventListener('touchmove', this.boundOnDragMove);
    document.addEventListener('touchend', this.boundOnDragEnd);

    event.preventDefault();
  }

  private onDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging || !this.dragConfig) return;

    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    this.dragConfig._dragX = this.dragOriginX + (clientX - this.dragStartX);
    this.dragConfig._dragY = this.dragOriginY + (clientY - this.dragStartY);
  }

  private onDragEnd(): void {
    this.isDragging = false;
    this.dragConfig = null;

    document.removeEventListener('mousemove', this.boundOnDragMove);
    document.removeEventListener('mouseup', this.boundOnDragEnd);
    document.removeEventListener('touchmove', this.boundOnDragMove);
    document.removeEventListener('touchend', this.boundOnDragEnd);
  }

  /**
   * Retorna o transform CSS para drag
   */
  getTranslate(config: SvMessageConfig): string {
    const x = config._dragX || 0;
    const y = config._dragY || 0;
    if (x === 0 && y === 0) return 'none';
    return `translate(${x}px, ${y}px)`;
  }

  /**
   * Fechar toast com stopPropagation
   */
  closeToast(event: Event, id: number): void {
    event.stopPropagation();
    this.dismissToast(id, false);
  }
}
